import * as R from "ramda"
import { email as validateEmail } from "@kiwicom/nitro/lib/services/input/validators"

import type {
  NewField,
  FieldPropsForSubmit,
  AdditionalContact,
  AdditionalContactCategory,
} from "./consts"

type NewFields = NewField[]
type AdditionalContacts = AdditionalContact[]

const arePropsEqual = (
  x:
    | FieldPropsForSubmit
    | {
        category: "email" | "phone"
        value: string
      },
  y:
    | FieldPropsForSubmit
    | {
        category: "email" | "phone"
        value: string
      },
) => x.category === y.category && x.value === y.value

export const emailValidator = (value: string): string => {
  if (value === "") {
    return ""
  }
  // validateEmail already uses react-intl but this component hasn't been migrated to react-intl
  // bug on nitro - flow types hasn't been updated to react-intl https://skypicker.slack.com/archives/C7T7QG7M5/p1679916566086089
  return validateEmail(value)?.id ?? ""
}

/** parsePhone to normalize from '+34 111 222' to '+34 111222' */
const composePhone = (code: string | null | undefined, value: string) => {
  return code ? `+${code} ${value}` : value
}

/** composeCode to compose from `{ code: "34", value: "111222"}` to '+34 111222' */
const parsePhone = (phone: string) => {
  if (!phone.startsWith("+")) {
    return phone
  }
  const [code, ...parts] = phone.split(" ")
  return `${code} ${parts.join("").trim()}`
}

export const validateNewFieldsAgainsOldFields = (
  newFields: NewFields,
  prevContacts: {
    value: string
    category: AdditionalContactCategory
  }[],
) => {
  // to track if at least one of the fields fails to validate
  let atLeastOneHasError = false

  const validatedFields = newFields.map<NewField>((item) => {
    const isEmail = item.category === "email"

    let error = ""

    // 1. check if the field passes the field-specific validation

    error = isEmail ? emailValidator(item.value) : ""

    if (error) {
      atLeastOneHasError = true
      return { ...item, error }
    }

    // 2. check if the field is unique to previous contacts

    const isDuplicate = prevContacts.some((addContact) => {
      if (addContact.category === "email" && isEmail) {
        return addContact.value === item.value
      }
      if (addContact.category === "phone" && item.category === "phone") {
        return parsePhone(addContact.value) === composePhone(item.code, item.value)
      }
      return false
    })

    if (isDuplicate) {
      atLeastOneHasError = true
      // @ts-expect-error: Flow to TS migration.
      error = __("mmb.contact.additional.error.is_duplicate")
    }

    return { ...item, error }
  })

  return { hasError: atLeastOneHasError, validatedFields }
}

// Fix this the next time the file is edited. (Flow to TS)
// eslint-disable-next-line @typescript-eslint/array-type
export const pickPropsForSubmit: (arg1: NewFields) => Array<FieldPropsForSubmit> = R.map(
  R.pick(["category", "comment", "value"]),
)

export const pickAdditionalContactsProps: (arg1: AdditionalContacts) => AdditionalContacts = R.map(
  R.pick(["category", "comment", "value", "id"]),
)

// Fix this the next time the file is edited. (Flow to TS)
// eslint-disable-next-line @typescript-eslint/array-type
const mapNewFields: (arg1: NewFields) => Array<FieldPropsForSubmit> = R.compose(
  R.uniqWith(arePropsEqual),
  R.map((field) => {
    // @ts-expect-error: Flow to TS migration.
    const value = field.category === "phone" ? composePhone(field.code, field.value) : field.value
    // @ts-expect-error: Flow to TS migration.
    return { category: field.category, value }
  }),
  // @ts-expect-error: Flow to TS migration.
  R.filter((field) => Boolean(field.value.trim())),
)

export default mapNewFields
